import { useEffect, useState } from 'react';
import { Mail, Send, AlertCircle, Plus, CalendarClock } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { newsletterApi, ApiError } from '@solara/api-client';
import type { NewsletterCampaignDto } from '@solara/api-client';

const STATUS_TONES: Record<string, 'success' | 'warning' | 'info' | 'neutral' | 'danger'> = {
  DRAFT: 'neutral',
  SCHEDULED: 'info',
  SENT: 'success',
  FAILED: 'danger',
};

export default function NewsletterPage() {
  const { t } = useTranslation('p1');
  const [campaigns, setCampaigns] = useState<NewsletterCampaignDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<{ subject: string; bodyHtml: string; bodyText: string } | null>(null);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      setCampaigns(await newsletterApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function create() {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      await newsletterApi.create(draft);
      setDraft(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('newsletter.createFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function send(id: string) {
    if (!window.confirm(t('newsletter.confirmSend'))) return;
    setBusy(true);
    try {
      await newsletterApi.send(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('newsletter.sendFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function schedule(id: string) {
    const when = window.prompt(t('newsletter.schedulePrompt'));
    if (!when) return;
    setBusy(true);
    try {
      await newsletterApi.schedule(id, when);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('newsletter.scheduleFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('newsletter.title')}</h1>
          <p className="text-fg-muted text-sm">{t('newsletter.subtitle')}</p>
        </div>
        <Button onClick={() => setDraft({ subject: '', bodyHtml: '', bodyText: '' })}>
          <Plus className="mr-2 h-4 w-4" />
          {t('newsletter.newCampaign')}
        </Button>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      {draft && (
        <Card className="rounded-2xl border-brand-500/30">
          <CardHeader>
            <CardTitle>{t('newsletter.compose')}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <Input
              label={t('newsletter.subject')}
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            />
            <label className="block text-sm">
              <span className="text-xs text-fg-muted">{t('newsletter.bodyHtml')}</span>
              <textarea
                className="w-full min-h-[10rem] mt-1 rounded border border-border bg-surface px-3 py-2 text-sm font-mono"
                value={draft.bodyHtml}
                onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value })}
                placeholder={t('newsletter.bodyPlaceholder')}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-fg-muted">{t('newsletter.bodyText')}</span>
              <textarea
                className="w-full min-h-[6rem] mt-1 rounded border border-border bg-surface px-3 py-2 text-sm"
                value={draft.bodyText}
                onChange={(e) => setDraft({ ...draft, bodyText: e.target.value })}
              />
            </label>
            <div className="flex gap-2">
              <Button onClick={create} disabled={busy}>
                {busy ? t('actions.saving') : t('newsletter.saveDraft')}
              </Button>
              <Button variant="ghost" onClick={() => setDraft(null)}>
                {t('actions.cancel')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            {t('newsletter.campaignsCount', { count: campaigns.length })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {campaigns.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('newsletter.noCampaigns')}</p>
          ) : (
            <ul className="divide-y divide-border">
              {campaigns.map((c) => (
                <li key={c.id} className="py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{c.subject}</p>
                      <p className="text-xs text-fg-muted">
                        {t('newsletter.createdOn', {
                          when: new Date(c.createdAt).toLocaleDateString(),
                        })}
                        {c.sentAt && (
                          <span>
                            {' · '}
                            {t('newsletter.sentOn', {
                              when: new Date(c.sentAt).toLocaleString(),
                            })}
                          </span>
                        )}
                        {c.scheduledFor && (
                          <span>
                            {' · '}
                            {t('newsletter.scheduledFor', {
                              when: new Date(c.scheduledFor).toLocaleString(),
                            })}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge tone={STATUS_TONES[c.status] ?? 'neutral'} className="text-[10px]">
                        {t(`newsletter.statuses.${c.status}`, { defaultValue: c.status })}
                      </Badge>
                      {c.status === 'DRAFT' && (
                        <>
                          <Button variant="ghost" onClick={() => void schedule(c.id)} disabled={busy}>
                            <CalendarClock className="mr-1 h-3 w-3" />
                            {t('newsletter.scheduleAction')}
                          </Button>
                          <Button onClick={() => void send(c.id)} disabled={busy}>
                            <Send className="mr-1 h-3 w-3" />
                            {t('newsletter.sendNow')}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
